var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { LitElement, html, css } from 'lit';
import { property, query, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { ifDefined } from 'lit/directives/if-defined.js';
/**
 * A file input styled to match KRTextField.
 *
 * Uses ElementInternals for form association, so it participates in form submission, validation,
 * and reset exactly like a native `<input type="file">`. On selection it submits the picked file(s)
 * under the field `name` (via a FormData form value), and mirrors the native input's validity
 * (e.g. `required`).
 *
 * Emits a composed `change` event on selection (re-dispatched from the host, since the inner
 * input's native `change` doesn't cross the shadow boundary). The current File(s) are also
 * available via the `files` property for scripting.
 *
 * A file input's value can't be set programmatically, so to show an already-saved file when the
 * form loads (before the user picks anything) bind the stored URL/path to `value` — the control
 * displays it in place of the placeholder. A fresh selection always takes precedence.
 */
export class KRFileField extends LitElement {
    constructor() {
        super();
        /**
         * The input label text
         */
        this.label = '';
        /**
         * The input name for form submission
         */
        this.name = '';
        /**
         * Accepted file types (forwarded to the native input's accept attribute)
         */
        this.accept = '';
        /**
         * Allow selecting multiple files
         */
        this.multiple = false;
        /**
         * Text shown when no file is selected
         */
        this.placeholder = 'No file chosen';
        /**
         * The current saved file, as the URL or path stored on the record. A file input's value can't be
         * set programmatically, so a form pre-populated from saved data binds it here (like every other
         * krubble field's `value`) and the control shows the file's name instead of the placeholder. A
         * fresh selection (`files`) always takes precedence.
         */
        this.value = '';
        /**
         * The trailing button label
         */
        this.buttonLabel = 'Choose File';
        /**
         * Whether the field is required
         */
        this.required = false;
        /**
         * Whether the field is disabled
         */
        this.disabled = false;
        /**
         * Helper text shown below the control
         */
        this.hint = '';
        /**
         * The selected files. Read to get the picked File objects; the displayed name(s) derive from it.
         */
        this.files = [];
        /**
         * Whether the user has picked a file since it was last loaded or marked pristine. Set on
         * selection, cleared by markPristine(). Used by <kr-form> to collect changed fields.
         */
        this.dirty = false;
        /**
         * Whether a <kr-form> save carrying this field's selection is in flight. Set by markSaving(),
         * cleared by markSaved()/markSaveFailed().
         */
        this.saving = false;
        this._touched = false;
        this._focused = false;
        this._handleInvalid = (e) => {
            e.preventDefault();
            this._touched = true;
        };
        this._internals = this.attachInternals();
    }
    connectedCallback() {
        super.connectedCallback();
        // Marks this as a krubble form field so <kr-form> can find its controls (.kr-form-field).
        this.classList.add('kr-form-field');
        this.addEventListener('invalid', this._handleInvalid);
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        this.removeEventListener('invalid', this._handleInvalid);
    }
    firstUpdated() {
        this._updateValidity();
    }
    updated(changedProperties) {
        if (changedProperties.has('required')) {
            this._updateValidity();
        }
    }
    // Form-associated custom element callbacks
    get form() {
        return this._internals.form;
    }
    get validity() {
        return this._internals.validity;
    }
    get validationMessage() {
        return this._internals.validationMessage;
    }
    get willValidate() {
        return this._internals.willValidate;
    }
    checkValidity() {
        return this._internals.checkValidity();
    }
    reportValidity() {
        return this._internals.reportValidity();
    }
    formResetCallback() {
        if (this._input) {
            this._input.value = '';
        }
        this.files = [];
        this._touched = false;
        this.dirty = false;
        this.saving = false;
        this._internals.setFormValue(null);
        this._internals.setValidity({});
    }
    // Clears the dirty flag so the field is no longer reported as changed.
    markPristine() {
        this.dirty = false;
    }
    // <kr-form> calls this when a save carrying this field's selection starts. Clearing dirty here
    // means a further selection during the request is tracked as new and goes out in the next save.
    // Unlike the other fields this one doesn't guard `value` while dirty: the user's edit lives in
    // `files`, which the display already prefers over `value`, so an external `value` set (the saved
    // URL) can't overwrite a pending selection.
    markSaving() {
        this.saving = true;
        this.dirty = false;
    }
    // <kr-form> calls this when the save succeeded. If the user picked again mid-flight the field is
    // dirty again and stays that way.
    markSaved() {
        this.saving = false;
    }
    // <kr-form> calls this when the save failed. Restore dirty so the next change retries the upload.
    markSaveFailed() {
        this.saving = false;
        this.dirty = true;
    }
    // Public methods for programmatic control
    open() {
        this._input?.click();
    }
    clear() {
        this.formResetCallback();
        this._updateValidity();
    }
    _updateValidity() {
        if (this._input) {
            this._internals.setValidity(this._input.validity, this._input.validationMessage, this._input);
        }
    }
    _handleClick() {
        if (this.disabled) {
            return;
        }
        this._input.click();
    }
    _handleFocus() {
        this._focused = true;
    }
    _handleBlur() {
        this._focused = false;
        this._touched = true;
    }
    _handleChange() {
        this.files = Array.from(this._input.files || []);
        this.dirty = true;
        // Submit the selected files under this field's name so a native form submit sends the file
        // bytes exactly as `<input type="file">` would.
        if (this.files.length) {
            const formData = new FormData();
            for (const file of this.files) {
                formData.append(this.name, file, file.name);
            }
            this._internals.setFormValue(formData);
        }
        else {
            this._internals.setFormValue(null);
        }
        this._updateValidity();
        // The native input's `change` fires inside the shadow root and, because change events are not
        // composed, never reaches consumers. Re-dispatch a composed one from the host so
        // `<kr-file-field @change=...>` works like a native file input.
        this.dispatchEvent(new Event('change', {
            bubbles: true,
            composed: true,
        }));
    }
    render() {
        let validationMessage = '';
        if (this._touched && this._input && !this._input.validity.valid) {
            validationMessage = this._input.validationMessage;
        }
        return html `
      <div class="wrapper">
        ${this.label
            ? html `
            <label for="input">
              ${this.label}
              ${this.required ? html `<span class="required" aria-hidden="true">*</span>` : ''}
            </label>
          `
            : ''}
        <div
          class=${classMap({
            'control': true,
            'control--focused': this._focused,
            'control--disabled': this.disabled,
            'control--invalid': this._touched && this._input && !this._input.validity.valid,
        })}
          @click=${this._handleClick}
        >
          <button
            type="button"
            class="button"
            ?disabled=${this.disabled}
            tabindex="-1"
          >
            ${this.buttonLabel}
          </button>
          <span
            class=${classMap({
            'filename': true,
            'filename--placeholder': !this.files.length && !this.value,
        })}
          >
            ${this.files.length
            ? this.files.map(file => file.name).join(', ')
            : this.value || this.placeholder}
          </span>
        </div>
        <input
          id="input"
          type="file"
          name=${this.name}
          accept=${ifDefined(this.accept || undefined)}
          ?multiple=${this.multiple}
          ?required=${this.required}
          ?disabled=${this.disabled}
          @change=${this._handleChange}
          @focus=${this._handleFocus}
          @blur=${this._handleBlur}
        />
        ${validationMessage
            ? html `<div class="validation-message">${validationMessage}</div>`
            : this.hint
                ? html `<div class="hint">${this.hint}</div>`
                : ''}
      </div>
    `;
    }
}
KRFileField.styles = css `
    :host {
      display: block;
      font-family: inherit;
    }

    .wrapper {
      display: flex;
      flex-direction: column;
      gap: 6px;
    }

    label {
      font-size: 14px;
      font-weight: 500;
      color: black;
    }

    .required {
      color: var(--kr-text-field-required-color, #ef4444);
      margin-left: 0.125rem;
    }

    /* Matches the text field's input box exactly. */
    .control {
      display: flex;
      align-items: center;
      gap: 12px;
      font-family: inherit;
      font-size: 14px;
      padding: 10px 12px;
      border: 1px solid var(--kr-text-field-border-color, #0000003d);
      border-radius: 8px;
      background-color: var(--kr-text-field-bg, #fff);
      color: var(--kr-text-field-color, #111827);
      cursor: pointer;
      transition: border-color 0.2s, box-shadow 0.2s;
    }

    .control--focused {
      outline: none;
      border-color: var(--kr-text-field-focus-border-color, #163052);
      box-shadow: 0 0 0 3px var(--kr-text-field-focus-ring-color, rgba(22, 48, 82, 0.1));
    }

    .control--disabled {
      background-color: var(--kr-text-field-disabled-bg, #f3f4f6);
      color: var(--kr-text-field-disabled-color, #9ca3af);
      cursor: not-allowed;
    }

    .control--invalid {
      border-color: var(--kr-text-field-error-border-color, #ef4444);
    }

    .control--invalid.control--focused {
      box-shadow: 0 0 0 3px var(--kr-text-field-error-ring-color, rgba(239, 68, 68, 0.1));
    }

    .filename {
      flex: 1;
      min-width: 0;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .filename--placeholder {
      color: var(--kr-text-field-placeholder-color, #9ca3af);
    }

    .button {
      flex-shrink: 0;
      font-family: inherit;
      font-size: 14px;
      font-weight: 600;
      padding: 0;
      border: none;
      background: none;
      color: var(--kr-file-field-button-color, #111827);
      cursor: inherit;
    }

    /* The real input drives the picker and focus ring but is never seen. */
    input {
      position: absolute;
      width: 1px;
      height: 1px;
      opacity: 0;
      pointer-events: none;
    }

    .validation-message {
      font-size: 0.75rem;
      color: var(--kr-text-field-error-color, #ef4444);
    }

    .hint {
      font-size: 0.75rem;
      color: var(--kr-text-field-helper-color, #6b7280);
    }
  `;
// Enable form association
KRFileField.formAssociated = true;
__decorate([
    property({ type: String })
], KRFileField.prototype, "label", void 0);
__decorate([
    property({ type: String })
], KRFileField.prototype, "name", void 0);
__decorate([
    property({ type: String })
], KRFileField.prototype, "accept", void 0);
__decorate([
    property({ type: Boolean })
], KRFileField.prototype, "multiple", void 0);
__decorate([
    property({ type: String })
], KRFileField.prototype, "placeholder", void 0);
__decorate([
    property({ type: String })
], KRFileField.prototype, "value", void 0);
__decorate([
    property({ type: String })
], KRFileField.prototype, "buttonLabel", void 0);
__decorate([
    property({ type: Boolean })
], KRFileField.prototype, "required", void 0);
__decorate([
    property({ type: Boolean })
], KRFileField.prototype, "disabled", void 0);
__decorate([
    property({ type: String })
], KRFileField.prototype, "hint", void 0);
__decorate([
    property({ attribute: false })
], KRFileField.prototype, "files", void 0);
__decorate([
    property({
        type: Boolean,
        reflect: true,
    })
], KRFileField.prototype, "dirty", void 0);
__decorate([
    property({
        type: Boolean,
        reflect: true,
    })
], KRFileField.prototype, "saving", void 0);
__decorate([
    state()
], KRFileField.prototype, "_touched", void 0);
__decorate([
    state()
], KRFileField.prototype, "_focused", void 0);
__decorate([
    query('input')
], KRFileField.prototype, "_input", void 0);
// Guarded registration: no-op if the tag is already defined, so a second copy of krubble on
// the page (e.g. the Operations shell loads krubble globally while an embedded app bundled its
// own copy) doesn't throw. First definition wins.
if (!customElements.get('kr-file-field')) {
    customElements.define('kr-file-field', KRFileField);
}
//# sourceMappingURL=file-field.js.map